/**
 * Client for the Seitiate+ backend's research integrations proxy
 * (`/api/v1/research/integrations/*`, which forwards to the managed research
 * worker with the caller's `X-Research-User`). Ported for the Research
 * connections tab.
 *
 * Unlike the Sensarra standalone site (which had a public same-origin worker
 * fallback), every Seitiate+ caller is authenticated, so this always goes
 * through `apiFetch` — the JWT is attached and the browser never holds the
 * worker bearer token.
 */
import { apiFetch } from '@/lib/api';
import { PLUS_API_URL } from '@/lib/env';
import type { Catalogue, Connection, TestResult } from '@/features/integrations/types';

const BASE = '/api/v1/research/integrations';

export function fetchCatalogue(): Promise<Catalogue> {
  return apiFetch<Catalogue>(`${BASE}/providers`);
}

export function saveConnection(
  providerId: string,
  fields: Record<string, string>,
  enabled = true,
  createNew = false,
): Promise<Connection> {
  return apiFetch<Connection>(`${BASE}/connections/${encodeURIComponent(providerId)}`, {
    method: 'PUT',
    body: JSON.stringify({ fields, enabled, create_new: createNew }),
  });
}

export function deleteConnection(providerId: string): Promise<{ deleted: boolean }> {
  return apiFetch<{ deleted: boolean }>(`${BASE}/connections/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
  });
}

export function testConnection(providerId: string): Promise<TestResult> {
  return apiFetch<TestResult>(`${BASE}/connections/${encodeURIComponent(providerId)}/test`, {
    method: 'POST',
  });
}

/**
 * OAuth start URL for a provider, routed through the Seitiate+ backend proxy.
 * NOTE: the backend `/integrations/oauth/{id}/start` proxy is a follow-up (the
 * worker↔Plus OAuth handshake needs a signed-state design); credential/MCP
 * providers — the entire molecular perimeter — are fully functional today.
 */
export function oauthStartUrl(providerId: string): string {
  return `${PLUS_API_URL}${BASE}/oauth/${encodeURIComponent(providerId)}/start`;
}
